import { useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  getPostAttachmentIdentity,
  reconcileAttachmentIdentities,
} from "@/entities/attachment";
import {
  getPostAttachments,
  getPostPlainBody,
  type Post,
  postNo,
} from "@/entities/post/types";
import type { ThreadChunkPost } from "@/entities/thread/types";
import { getImageUrl } from "@/entities/thread/utils";
import { isR18ImageHidden } from "@/features/board/utils/r18Spoiler";
import { createMediator } from "@/shared/lib/componentHierarchy";
import {
  useComponentNode,
  useHierarchyLifetime,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";
export interface BoardPostProps {
  post: Post | ThreadChunkPost;
  isOp?: boolean;
  isR18Thread: boolean;
  showR18: boolean;
}
type BoardPostEvent =
  | { type: "board-post.sync"; identities: string[] }
  | { type: "board-post.reveal"; identity: string };
export function useBoardPostPresenter({
  post,
  isOp = false,
  isR18Thread,
  showR18,
}: BoardPostProps) {
  const attachments = useMemo(() => getPostAttachments(post), [post]);
  const identities = useMemo(
    () =>
      attachments.map((attachment) =>
        getPostAttachmentIdentity(post.id, attachment),
      ),
    [attachments, post.id],
  );
  const environment = useRef({ isR18Thread, showR18 });
  useLayoutEffect(() => {
    environment.current = { isR18Thread, showR18 };
  });
  const [mediator] = useState(() =>
    createMediator<
      { identities: string[]; revealed: Set<string> },
      BoardPostEvent
    >({ identities, revealed: new Set() }, (state, event) => {
      if (event.type === "board-post.sync")
        return {
          status: "handled",
          state: {
            identities: event.identities,
            revealed: reconcileAttachmentIdentities(
              state.revealed,
              event.identities,
            ),
          },
        };
      return {
        status: "handled",
        state: isR18ImageHidden({
          hasAttachment: state.identities.includes(event.identity),
          isR18Thread: environment.current.isR18Thread,
          showR18: environment.current.showR18,
          revealed: state.revealed.has(event.identity),
        })
          ? { ...state, revealed: new Set(state.revealed).add(event.identity) }
          : state,
      };
    }),
  );
  useHierarchyLifetime(mediator);
  const node = useComponentNode((event) => {
    if (event.type !== "board-post.reveal") return "unhandled";
    mediator.dispatch(event as BoardPostEvent);
    return "handled";
  });
  useLayoutEffect(() => {
    mediator.dispatch({ type: "board-post.sync", identities });
  }, [mediator, identities]);
  const { revealed } = useMediatorSnapshot(mediator);
  return {
    node,
    isOp,
    displayNo: postNo(post),
    body: getPostPlainBody(post),
    hasRevealed: revealed.size > 0,
    revealCount: revealed.size,
    attachments: attachments.map((attachment, index) => ({
      identity: identities[index] ?? "",
      src: getImageUrl(attachment, false),
      isHidden: isR18ImageHidden({
        hasAttachment: true,
        isR18Thread,
        showR18,
        revealed: revealed.has(identities[index] ?? ""),
      }),
    })),
  };
}
